import { useCallback, useRef, useState } from 'react';
import {
  SafePolygonOptions,
  UseTransitionStatusProps,
  UseTransitionStylesProps,
  safePolygon,
} from '@floating-ui/react';
import {
  usePopoverInteractions,
  usePopover,
  UsePopoverInteractionsOptions,
  useTransitions,
} from '../hooks';
import { PopoverBase, PopoverBaseProps } from '../PopoverBase';
import { PopoverContent } from '../PopoverContent';

type mouseEventType = React.MouseEvent<HTMLElement>;
type focusEventType = React.FocusEvent<HTMLElement>;
type keyboardEventType = React.KeyboardEvent<HTMLElement>;

const ARROW_SIZE = 12;
export interface PopoverWrapperProps extends Omit<PopoverBaseProps, 'children'> {
  children: React.ReactElement;
  popoverContent: React.ReactNode;
  behavior?: 'click' | 'hover';
  interactions?: Omit<UsePopoverInteractionsOptions, 'context'>;
  onMouseEnter?: (e: mouseEventType) => void;
  onMouseLeave?: (e: mouseEventType) => void;
  onBlur?: (e: focusEventType) => void;
  onClick?: (e: mouseEventType) => void;
  onFocus?: (e: focusEventType) => void;
  onKeyDown?: (e: keyboardEventType) => void;
  onKeyUp?: (e: keyboardEventType) => void;
  closeOnFocusOut?: boolean;
  transitionStylesConfig?: UseTransitionStylesProps;
  transitionStatusConfig?: UseTransitionStatusProps;
  forceIsMounted?: boolean;
  safePolygonOptions?: SafePolygonOptions;
}

/**
 * PopoverWrapper Component
 *
 * A wrapper component that wraps the anchor element.
 * It handles the behavior of the popover and the anchor element.
 * Popover's content is displayed when the anchor element is clicked or hovered.
 *
 * @param children - The anchor element of the popover. Must be a React.ReactElement.
 * @param popoverContent - The content of the popover. Must be a React.ReactNode.
 * @param id - The id of the popover
 * @param offset - The offset of the popover - https://floating-ui.com/docs/offset
 * @param shift - The shift of the popover - https://floating-ui.com/docs/shift
 * @param flip - The flip of the popover - https://floating-ui.com/docs/flip
 * @param placement - The placement of the popover - https://floating-ui.com/docs/arrow#placement
 * @param strategy - The strategy of the popover - https://floating-ui.com/docs/usefloating#strategy
 * @param transform - The transform of the popover - https://floating-ui.com/docs/usefloating#transform
 * @param hideArrow - The hideArrow of the popover
 * @param arrowColor - The arrow color of the popover. **@deprecated** Use design token --tooltip-arrow / theme. Arrow color is token-driven for tooltips; avoid for new code.
 * @param arrowPadding - The padding between the arrow element and the floating element edges - https://floating-ui.com/docs/arrow#padding
 * @param behavior - The behavior of the popover. "click" or "hover"
 * @param interactions - The interactions of the popover - https://floating-ui.com/docs/useinteractions#usage
 * @param onMouseEnter - The onMouseEnter event of the anchor element
 * @param onMouseLeave - The onMouseLeave event of the anchor element
 * @param onBlur - The onBlur event of the anchor element
 * @param onClick - The onClick event of the anchor element
 * @param onFocus - The onFocus event of the anchor element
 * @param onKeyDown - The onKeyDown event of the anchor element
 * @param onKeyUp - The onKeyUp event of the anchor element
 * @param closeOnFocusOut - Boolean to decide if the popover should close on focus out. Default is true.
 * @param transitionStylesConfig - It handles the transition styles through javascript - https://floating-ui.com/docs/usetransition#usetransitionstyles
 * @param transitionStatusConfig - It handles the transition duration and styles through css - https://floating-ui.com/docs/usetransition#usetransitionstatus
 * @param forceIsMounted - Boolean to force popoverContent to be mounted. Default false
 * @param safePolygonOptions - Options to be passed to safePolygon in handleClose in useHover - https://floating-ui.com/docs/usehover#safepolygon
 *
 * @default
 * - offset: 10
 * - shift: 0
 * - flip: true
 * - placement: 'top'
 * - strategy: 'absolute'
 * - transform: 'translate'
 * - hideArrow: false
 * - arrowColor: deprecated; use design tokens. When undefined, arrow uses --tooltip-arrow or --color-fg-primary.
 * - arrowPadding: 0
 * - behavior: 'click'
 * - interactions: {
 *     hoverOptions: { enabled: false },
 *     clickOptions: { enabled: true, toggle: true },
 *     focusOptions: { enabled: false },
 *     dismissOptions { enabled: true },
 *     roleOptions: { enabled: true, role: "dialog" }
 *   }
 *
 */
export const PopoverWrapper: React.FC<PopoverWrapperProps> = ({
  children,
  popoverContent,
  id,
  offset,
  shift,
  flip,
  placement,
  strategy,
  transform,
  hideArrow,
  arrowColor,
  arrowPadding,
  behavior = 'click',
  interactions,
  onMouseEnter,
  onMouseLeave,
  onBlur,
  onClick,
  onFocus,
  onKeyDown,
  onKeyUp,
  closeOnFocusOut = true,
  transitionStylesConfig,
  transitionStatusConfig,
  forceIsMounted = false,
  safePolygonOptions,
  ...rest
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const arrowRef = useRef<SVGSVGElement>(null) as React.MutableRefObject<SVGSVGElement>;

  // Legacy refs maintained for backward compatibility - these mirror the floating-ui refs
  // and are synced via setReferenceRef/setFloatingRef callbacks below
  // Investigate removing these in future release if they are not being used
  const anchorRef = useRef<HTMLElement | null>(null);
  const popoverRef = useRef<HTMLElement | null>(null);

  // Popover hooks
  const { refs, floatingStyles, context } = usePopover({
    offset,
    shift,
    flip,
    placement,
    strategy,
    transform,
    isOpen,
    onOpenChange: setIsOpen,
    arrowPadding,
    arrowRef,
  });

  // Sync the floating-ui refs with your local refs
  const setReferenceRef = useCallback(
    (node: HTMLElement | null) => {
      anchorRef.current = node;
      refs.setReference(node);
    },
    [refs],
  );

  const setFloatingRef = useCallback(
    (node: HTMLElement | null) => {
      popoverRef.current = node;
      refs.setFloating(node);
    },
    [refs],
  );

  // Interactions hooks
  const { getReferenceProps, getFloatingProps } = usePopoverInteractions({
    context,
    ...interactions,
    hoverOptions: {
      enabled: behavior === 'hover',
      handleClose: safePolygon({
        ...safePolygonOptions,
      }),
      ...interactions?.hoverOptions,
    },
    clickOptions: {
      enabled: behavior === 'click',
      toggle: true,
      ...interactions?.clickOptions,
    },
    focusOptions: {
      enabled: false,
      ...interactions?.focusOptions,
    },
  });

  // reference events
  const handleReferenceBlur = (e: React.FocusEvent<HTMLElement>) => {
    const target = e.relatedTarget as HTMLElement;
    const isInsideOfPopoverContent = target?.closest('.vds-popover');

    if (!isInsideOfPopoverContent && closeOnFocusOut) {
      setIsOpen(false);
    }
    if (onBlur) onBlur(e);
  };

  const handleReferenceOnKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.code === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      setIsOpen(false);
    }

    // stop propagation and let the children button handle the space/enter key
    if (e.code === 'Space' || e.code === 'Enter') {
      e.stopPropagation();

      // keep click behavior to toggle tooltip
      if (behavior === 'click') {
        e.preventDefault();
        setIsOpen(open => (interactions?.clickOptions?.toggle === false ? true : !open));
      }
    }

    if (onKeyDown) onKeyDown(e);
  };

  // content events
  const handleContentBlur = (e: React.FocusEvent<HTMLElement>) => {
    const isOutsideOfContent = e.target.closest('.vds-popover') === popoverRef.current;
    const isAnchorElement = e.relatedTarget === anchorRef.current;

    if (isOutsideOfContent && !isAnchorElement && closeOnFocusOut) {
      setIsOpen(false);
    }
  };

  const { transitionStyles, transitionStatus } = useTransitions({
    context,
    transitionStylesConfig: {
      duration: { open: 0, close: 0 }, // default duration is 0 for open and close states.
      ...transitionStylesConfig,
    },
    transitionStatusConfig: {
      duration: { open: 0, close: 0 }, // default duration is 0 for open and close states.
      ...transitionStatusConfig,
    },
  });

  // show tooltip when the children is focused (only for hover behavior, not click)
  const handleReferenceFocusCapture = () => {
    if (behavior === 'hover') {
      setIsOpen(true);
    }
  };

  // hide tooltip when the children is blurred (only for hover behavior, not click)
  const handleReferenceBlurCapture = () => {
    if (behavior === 'hover' && closeOnFocusOut) {
      setIsOpen(false);
    }
  };

  const isMounted = forceIsMounted || transitionStyles.isMounted || transitionStatus.isMounted;

  return (
    <>
      <div
        ref={setReferenceRef}
        {...getReferenceProps({
          onMouseEnter,
          onMouseLeave,
          onBlur: handleReferenceBlur,
          onClick,
          onFocus,
          onKeyDown: handleReferenceOnKeyDown,
          onFocusCapture: handleReferenceFocusCapture,
          onBlurCapture: handleReferenceBlurCapture,
          onKeyUp,
        })}>
        {children}
      </div>
      {isMounted && (
        <PopoverBase
          id={id}
          ref={setFloatingRef}
          anchorRef={anchorRef}
          arrowRef={arrowRef}
          arrowPadding={arrowPadding}
          arrowColor={arrowColor}
          hideArrow // hide arrow in base popover to avoid duplicated arrow
          offset={offset}
          shift={shift}
          flip={flip}
          strategy={strategy}
          transform={transform}
          onOpenChange={setIsOpen}
          placement={placement}
          floatingStyles={floatingStyles}
          refs={refs}
          context={context}
          isHidden={forceIsMounted && !transitionStatus.isMounted}
          {...rest}
          {...getFloatingProps()}>
          <PopoverContent
            onBlur={handleContentBlur}
            style={transitionStyles.styles}
            data-placement={placement}
            data-status={transitionStatus.status}
            context={rest.context ?? context}
            arrowRef={arrowRef}
            hideArrow={hideArrow}
            arrowSize={ARROW_SIZE}
            arrowColor={arrowColor}>
            {popoverContent}
          </PopoverContent>
        </PopoverBase>
      )}
    </>
  );
};
